// frontend/app/components/lessonPlans/authoring/BlockEditor.js
//
// Renders the editor for one block entirely from its entry in blockSchemas.js.
// There is deliberately no per-block-type component here: a new block type is
// a schema entry, not a new form.
//
// The header carries the block's evidence weight, because what a block is
// worth towards mastery is not visible anywhere in the fields themselves — an
// explanation and a prediction look identical while you are writing them.

import React from "react";
import { StyleSheet, Text, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import AssetRefField from "./BlockAssetRefField";
import { OptionsField, OrderItemsField } from "./BlockAnswerFields";
import { PromptListField, ToggleField } from "./BlockPromptFields";
import {
  ChipSelect,
  Field,
  IconButton,
  StringListField,
  TextField,
} from "./WizardFields";
import { evidenceLevelFor, FIELD, schemaFor, validateBlock } from "./blockSchemas";
import { getSectionForPhase, getSectionMeta } from "../lessonPlanTheme";

/** "1.5× · Strongest evidence", or nothing for a block that records no answer. */
const EvidenceBadge = ({ colors, weight, t }) => {
  const translate = t || ((key) => key);
  const level = evidenceLevelFor(weight);
  if (!level) return null;

  const accent = weight > 0 ? colors.success : colors.textMuted;
  return (
    <View style={[styles.evidence, { borderColor: accent }]}>
      <Text style={[styles.evidenceText, { color: accent }]}>
        {weight > 0 ? `${weight}× ` : ""}
        {translate(level.label)}
      </Text>
    </View>
  );
};

const BlockEditor = ({
  colors,
  block,
  concepts = [],
  boardTopicId,
  catalog,
  onChange,
  onRemove,
  onMove,
  isFirst,
  isLast,
  t,
}) => {
  const translate = t || ((key) => key);
  const schema = schemaFor(block.type);
  const patch = (fields) => onChange({ ...block, ...fields });

  if (!schema) {
    return (
      <View style={[styles.block, { borderColor: colors.danger }]}>
        <Text style={{ color: colors.danger }}>
          {translate("Unsupported block type")}: {block.type}
        </Text>
      </View>
    );
  }

  const problems = validateBlock(block, {
    conceptKeys: concepts.map((c) => c.concept_key),
    t,
  });
  // The section the type's home phase belongs to; phases are never shown.
  const phase = schema.phase
    ? getSectionMeta(getSectionForPhase(schema.phase))
    : null;

  return (
    <View
      style={[
        styles.block,
        {
          backgroundColor: colors.subtleBg,
          borderColor: problems.length ? colors.teacherTipBorder : colors.border,
        },
      ]}
    >
      <View style={styles.blockHeader}>
        <MaterialCommunityIcons name={schema.icon} size={15} color={colors.accentPrimary} />
        <Text style={[styles.blockType, { color: colors.textPrimary }]}>
          {translate(schema.label)}
        </Text>
        {phase ? (
          <Text style={[styles.phaseTag, { color: phase.accent }]}>
            {translate(phase.label)}
          </Text>
        ) : null}
        {schema.teacherOnly ? (
          <Text style={[styles.teacherOnly, { color: colors.warning }]}>
            {translate("Teacher only")}
          </Text>
        ) : null}
        <EvidenceBadge colors={colors} weight={schema.evidenceWeight} t={t} />
        <View style={{ flex: 1 }} />
        <IconButton
          colors={colors}
          icon="chevron-up"
          label={translate("Move block up")}
          disabled={isFirst}
          onPress={() => onMove(-1)}
        />
        <IconButton
          colors={colors}
          icon="chevron-down"
          label={translate("Move block down")}
          disabled={isLast}
          onPress={() => onMove(1)}
        />
        <IconButton
          colors={colors}
          icon="delete-outline"
          label={translate("Remove block")}
          danger
          onPress={onRemove}
        />
      </View>

      {schema.fields.map((field) => {
        const key = `${block.id}-${field.key}`;

        if (field.kind === FIELD.OPTIONS || field.kind === FIELD.MULTI_OPTIONS) {
          return (
            <Field key={key} colors={colors} label={translate(field.label)} required={field.required}>
              <OptionsField
                colors={colors}
                block={block}
                field={field}
                onPatch={patch}
                multi={field.kind === FIELD.MULTI_OPTIONS}
                t={t}
              />
            </Field>
          );
        }

        if (field.kind === FIELD.ORDER_ITEMS) {
          return (
            <Field
              key={key}
              colors={colors}
              label={translate(field.label)}
              hint={field.hint ? translate(field.hint) : undefined}
              required={field.required}
            >
              <OrderItemsField
                colors={colors}
                block={block}
                field={field}
                onPatch={patch}
                t={t}
              />
            </Field>
          );
        }

        if (field.kind === FIELD.PROMPT_LIST) {
          return (
            <Field
              key={key}
              colors={colors}
              label={translate(field.label)}
              hint={field.hint ? translate(field.hint) : undefined}
              required={field.required}
            >
              <PromptListField
                colors={colors}
                block={block}
                field={field}
                onPatch={patch}
                t={t}
              />
            </Field>
          );
        }

        if (
          field.kind === FIELD.PWO_REF ||
          field.kind === FIELD.VIDEO_REF ||
          field.kind === FIELD.TOPIC_QUIZ_REF
        ) {
          return (
            <AssetRefField
              key={key}
              colors={colors}
              block={block}
              kind={field.kind}
              onPatch={patch}
              boardTopicId={boardTopicId}
              catalog={catalog}
              t={t}
            />
          );
        }

        if (field.kind === FIELD.CONCEPT) {
          return (
            <Field
              key={key}
              colors={colors}
              label={translate(field.label)}
              hint={field.hint ? translate(field.hint) : undefined}
            >
              <ChipSelect
                colors={colors}
                // Sourced from the lesson's declared concepts and nothing else,
                // so an undeclared key cannot be typed in the first place.
                options={[
                  { value: null, label: translate("None") },
                  ...concepts.map((c) => ({ value: c.concept_key, label: c.label || c.concept_key })),
                ]}
                value={block.concept_key ?? null}
                onChange={(concept_key) => patch({ concept_key })}
                accessibilityLabel={translate(field.label)}
              />
            </Field>
          );
        }

        if (field.kind === FIELD.CHIPS) {
          return (
            <Field
              key={key}
              colors={colors}
              label={translate(field.label)}
              hint={field.hint ? translate(field.hint) : undefined}
            >
              <ChipSelect
                colors={colors}
                options={field.options.map((o) => ({ ...o, label: translate(o.label) }))}
                value={block[field.key] ?? field.defaultValue}
                onChange={(value) => patch({ [field.key]: value })}
                accessibilityLabel={translate(field.label)}
              />
            </Field>
          );
        }

        if (field.kind === FIELD.CHOICE) {
          return (
            <Field
              key={key}
              colors={colors}
              label={translate(field.label)}
              hint={field.hint ? translate(field.hint) : undefined}
            >
              <ChipSelect
                colors={colors}
                options={field.options.map((o) => ({ ...o, label: translate(o.label) }))}
                value={block[field.key] ?? field.default}
                onChange={(value) => patch({ [field.key]: value })}
                accessibilityLabel={translate(field.label)}
              />
            </Field>
          );
        }

        if (field.kind === FIELD.TOGGLE) {
          return (
            <Field
              key={key}
              colors={colors}
              label={translate(field.label)}
              hint={field.hint ? translate(field.hint) : undefined}
            >
              <ToggleField
                colors={colors}
                label={translate(field.label)}
                value={block[field.key] ?? field.defaultValue}
                onChange={(value) => patch({ [field.key]: value })}
                t={t}
              />
            </Field>
          );
        }

        if (field.kind === FIELD.STRING_LIST) {
          return (
            <Field
              key={key}
              colors={colors}
              label={translate(field.label)}
              hint={field.hint ? translate(field.hint) : undefined}
              required={field.required}
            >
              <StringListField
                colors={colors}
                items={block[field.key] || []}
                onChange={(items) => patch({ [field.key]: items })}
                placeholder={translate(field.placeholder || "")}
                addLabel={field.addLabel || "Add"}
                ordered={Boolean(field.ordered)}
                t={t}
              />
            </Field>
          );
        }

        return (
          <Field
            key={key}
            colors={colors}
            label={translate(field.label)}
            hint={field.hint ? translate(field.hint) : undefined}
            required={field.required}
          >
            <TextField
              colors={colors}
              value={block[field.key]}
              onChange={(value) => patch({ [field.key]: value })}
              placeholder={translate(field.placeholder || "")}
              multiline={field.kind === FIELD.LONG_TEXT}
              keyboardType={field.kind === FIELD.NUMBER ? "numeric" : undefined}
            />
          </Field>
        );
      })}

      {problems.length ? (
        <View
          style={[
            styles.problems,
            { backgroundColor: colors.teacherTipBg, borderColor: colors.teacherTipBorder },
          ]}
        >
          {problems.map((problem) => (
            <Text
              key={problem}
              style={[styles.problemText, { color: colors.teacherTipText }]}
            >
              • {problem}
            </Text>
          ))}
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  block: { borderRadius: 12, borderWidth: 1, padding: 14, marginBottom: 10 },
  blockHeader: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 7,
    marginBottom: 12,
  },
  blockType: { fontSize: 12, fontWeight: "800" },
  phaseTag: {
    fontSize: 9,
    fontWeight: "800",
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  teacherOnly: {
    fontSize: 9,
    fontWeight: "800",
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  evidence: { borderRadius: 999, borderWidth: 1, paddingHorizontal: 7, paddingVertical: 2 },
  evidenceText: { fontSize: 9, fontWeight: "800", letterSpacing: 0.3 },

  problems: { borderRadius: 9, borderWidth: 1, padding: 10, marginTop: 4 },
  problemText: { fontSize: 11, lineHeight: 17 },
});

export default BlockEditor;
